import { Helmet } from "react-helmet";
import { Link, useLocation } from "react-router-dom";
import { Camera, Store, MapPin, Upload, Cpu, Smartphone } from "lucide-react";
import { usePortalLanguage, tLang } from "@/contexts/PortalLanguageContext";

const CDN = "https://pub-f22312f1902b4c5b98d85bf07b70d4e9.r2.dev";

// Brand tokens
const NAVY  = "#1A1A2E";
const EMBER = "#FF6B35";
const TEAL  = "#00BFA5";

export default function HowItWorks() {
  const { t } = usePortalLanguage();
  const { pathname } = useLocation();
  const locale = pathname.startsWith("/fr") ? "fr" : pathname.startsWith("/es") ? "es" : "en";

  const consumerSteps = [
    {
      icon:  Camera,
      title: t("hiw.consumers.step1.title", "Point your phone at the space"),
      desc:  t("hiw.consumers.step1.desc",  "Open Fyrris and aim your camera at any room. The app reads the space in seconds — no tape measure, no guesswork."),
    },
    {
      icon:  Store,
      title: t("hiw.consumers.step2.title", "Browse products from local HPB retailers"),
      desc:  t("hiw.consumers.step2.desc",  "Explore fireplaces, grills, and patio furniture from showrooms near you. Everything in the catalog is available to buy locally."),
    },
    {
      icon:  MapPin,
      title: t("hiw.consumers.step3.title", "See it in AR — then visit the showroom"),
      desc:  t("hiw.consumers.step3.desc",  "Place any product in your room at true scale. When you're confident, the app shows you exactly which store carries it."),
    },
  ];

  const retailerSteps = [
    {
      icon:  Upload,
      title: t("hiw.retailers.step1.title", "Onboard your products"),
      desc:  t("hiw.retailers.step1.desc",  "Upload your catalog to the Fyrris portal. Basic product photos and specs are all you need to start. Our team handles the 3D processing."),
    },
    {
      icon:  Cpu,
      title: t("hiw.retailers.step2.title", "Customers visualize at home"),
      desc:  t("hiw.retailers.step2.desc",  "Shoppers place your fireplaces, grills, and patio products in their space via AR — before ever visiting a showroom."),
    },
    {
      icon:  Smartphone,
      title: t("hiw.retailers.step3.title", "They arrive ready to buy"),
      desc:  t("hiw.retailers.step3.desc",  "Geolocation guides customers to your nearest location. They walk in with measurements, screenshots, and confidence."),
    },
  ];

  return (
    <>
      <Helmet>
        <title>{tLang(locale, "hiw.meta.title", "How Fyrris Works")}</title>
        <meta name="description" content={tLang(locale, "hiw.meta.description")} />
        <meta name="keywords" content="how Fyrris works, AR visualization steps, HPB retailer onboarding, fireplace AR app, augmented reality shopping" />
        <meta property="og:title" content={tLang(locale, "hiw.meta.ogTitle")} />
        <meta property="og:description" content={tLang(locale, "hiw.meta.ogDescription")} />
        <meta property="og:image" content="https://www.fyrris.com/fyrris-social-banner.png" />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={tLang(locale, "hiw.meta.ogTitle")} />
        <meta name="twitter:description" content={tLang(locale, "hiw.meta.ogDescription")} />
      </Helmet>

      {/* ── HERO ─────────────────────────────────────────────────────────────── */}
      <section style={{ position: "relative", height: "60vh", overflow: "hidden" }}>
        <img
          src={`${CDN}/hero/How%20it%20works%20page%20Hero.png`}
          alt="Customer using the Fyrris AR app to visualize products in their home"
          loading="eager"
          style={{
            position: "absolute",
            inset: 0,
            width: "100%",
            height: "100%",
            objectFit: "cover",
            zIndex: 0,
          }}
        />
        <div
          style={{
            position: "absolute",
            inset: 0,
            background: "linear-gradient(to bottom, rgba(26,26,46,0.8) 0%, rgba(26,26,46,0.6) 100%)",
            zIndex: 1,
          }}
        />
        <div
          style={{
            position: "relative",
            zIndex: 10,
            height: "100%",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            textAlign: "center",
            padding: "0 1rem",
          }}
        >
          <div style={{ maxWidth: "48rem" }}>
            <p
              className="inline-block text-xs font-semibold tracking-widest uppercase mb-5 px-3 py-1 rounded-full"
              style={{ backgroundColor: `${EMBER}22`, color: EMBER }}
            >
              {t("hiw.hero.eyebrow", "How It Works")}
            </p>
            <h1 style={{ color: "white", fontSize: "clamp(2rem, 5vw, 3.5rem)", fontWeight: "bold", lineHeight: 1.2, marginBottom: "1.25rem" }}>
              {t("hiw.hero.headline", "From curious to confident.")}
            </h1>
            <p style={{ color: "#D1D5DB", fontSize: "1.125rem", maxWidth: "36rem", margin: "0 auto", lineHeight: 1.7 }}>
              {t(
                "hiw.hero.subhead",
                "Fyrris bridges homeowners and HPB specialty retailers through augmented reality — making buying decisions easier and showroom visits more rewarding."
              )}
            </p>
          </div>
        </div>
      </section>

      {/* ── SECTION 1: FOR HOMEOWNERS ────────────────────────────────────────── */}
      <section className="relative z-[20] py-20 md:py-28 px-4 sm:px-6 lg:px-8 bg-gray-950">
        <div className="max-w-7xl mx-auto">
          <p
            className="text-xs font-semibold uppercase tracking-widest text-center mb-3"
            style={{ color: TEAL }}
          >
            {t("hiw.consumers.eyebrow", "For Homeowners")}
          </p>
          <h2 className="text-3xl md:text-4xl font-bold text-white text-center mb-4">
            {t("hiw.consumers.title", "See it before you buy it.")}
          </h2>
          <p className="text-center text-gray-300 mb-14 max-w-xl mx-auto">
            {t("hiw.consumers.subtitle", "Free on iOS and Android. No account required.")}
          </p>

          <div className="grid md:grid-cols-3 gap-8">
            {consumerSteps.map(({ icon: Icon, title, desc }, idx) => (
              <div key={title} className="relative flex flex-col gap-5 bg-gray-900 rounded-xl p-6">
                <div
                  className="w-14 h-14 rounded-full flex items-center justify-center text-white font-bold text-base flex-shrink-0"
                  style={{ backgroundColor: TEAL }}
                >
                  {idx + 1}
                </div>
                <Icon className="w-7 h-7" style={{ color: TEAL }} />
                <h3 className="text-xl font-semibold text-white">{title}</h3>
                <p className="text-gray-300 text-sm leading-relaxed">{desc}</p>
                {idx < consumerSteps.length - 1 && (
                  <div
                    className="hidden md:block absolute top-7 left-full w-8 h-px"
                    style={{ backgroundColor: `${TEAL}44` }}
                  />
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── SECTION 2: FOR RETAILERS ─────────────────────────────────────────── */}
      <section
        className="relative z-[20] py-20 md:py-28 px-4 sm:px-6 lg:px-8"
        style={{
          backgroundColor: NAVY,
          backgroundImage: `radial-gradient(ellipse 60% 40% at 50% 100%, ${EMBER}0d, transparent)`,
        }}
      >
        <div className="max-w-7xl mx-auto">
          <p
            className="text-xs font-semibold uppercase tracking-widest text-center mb-3"
            style={{ color: EMBER }}
          >
            {t("hiw.retailers.eyebrow", "For Retailers")}
          </p>
          <h2 className="text-3xl md:text-4xl font-bold text-white text-center mb-4">
            {t("hiw.retailers.title", "Turn AR browsers into showroom buyers.")}
          </h2>
          <p className="text-center text-gray-300 mb-14 max-w-xl mx-auto">
            {t("hiw.retailers.subtitle", "Three steps from catalog upload to confident customer.")}
          </p>

          <div className="grid md:grid-cols-3 gap-8">
            {retailerSteps.map(({ icon: Icon, title, desc }, idx) => (
              <div key={title} className="relative flex flex-col gap-5 bg-gray-900 rounded-xl p-6">
                <div
                  className="w-14 h-14 rounded-full flex items-center justify-center text-white font-bold text-base flex-shrink-0"
                  style={{ backgroundColor: EMBER }}
                >
                  {idx + 1}
                </div>
                <Icon className="w-7 h-7" style={{ color: EMBER }} />
                <h3 className="text-xl font-semibold text-white">{title}</h3>
                <p className="text-gray-300 text-sm leading-relaxed">{desc}</p>
                {idx < retailerSteps.length - 1 && (
                  <div
                    className="hidden md:block absolute top-7 left-full w-8 h-px"
                    style={{ backgroundColor: `${EMBER}44` }}
                  />
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── BOTTOM CTA ───────────────────────────────────────────────────────── */}
      <section className="relative z-[20] py-20 md:py-28 px-4 sm:px-6 lg:px-8 bg-gray-950">
        <div className="max-w-2xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            {t("hiw.cta.headline", "Ready to get started?")}
          </h2>
          <p className="text-gray-300 mb-10 leading-relaxed">
            {t(
              "hiw.cta.body",
              "Whether you're a homeowner who wants to visualize before buying, or a retailer who wants qualified foot traffic — Fyrris is for you."
            )}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to={locale === "fr" ? "/fr/app" : locale === "es" ? "/es/app" : "/app"}
              className="inline-flex items-center justify-center rounded-full px-8 py-3 text-base font-medium text-white whitespace-nowrap transition hover:opacity-90"
              style={{ backgroundColor: TEAL }}
            >
              {t("hiw.cta.homeowners", "Get Early Access")}
            </Link>
            <Link
              to={locale === "fr" ? "/fr/detaillants/demo" : locale === "es" ? "/es/minoristas/demo" : "/retailers/demo"}
              className="inline-flex items-center justify-center rounded-full px-8 py-3 text-base font-medium text-white whitespace-nowrap transition hover:opacity-90"
              style={{ backgroundColor: EMBER, boxShadow: "0 4px 14px rgba(255,107,53,0.3)" }}
            >
              {t("hiw.cta.retailers", "Get Started as a Retailer")}
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}
